import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@osd/ui/card';
import { Heading } from '@osd/ui/heading';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/getting-started — how to use `@osd/ui` in your app.
 *
 * Three consumer shapes; pick the one that matches your build:
 *
 *   1. Workspace consumer (you're in a pnpm monorepo that vendors @osd/ui
 *      as `workspace:*`). Subpath imports only — no barrel (root/osd#20).
 *   2. Browser-pack consumer (any web page that wants plain DOM + ESM,
 *      no build step). Loads the served bundle from bugfix.pro/_ui/.
 *   3. Vite / esbuild consumer (you want npm-style install). Install
 *      the tarball directly from the served URL — npm fetches it over
 *      HTTPS and unpacks it into node_modules.
 *
 * Each shape covers: import the CSS once, import each primitive by
 * its subpath, no barrel. Design tokens (--osd-*) override at any
 * scope to re-theme.
 */
export const Route = createFileRoute('/ui/getting-started')({
  component: GettingStarted,
});

function GettingStarted() {
  return (
    <Stack gap={6}>
      <Section
        title="Getting started"
        hint="Pick the shape that matches your build. All three use the same component API; only the install step differs."
      >
        <Stack gap={3}>
          <Card>
            <Heading level={3}>1. Workspace consumer (recommended)</Heading>
            <Text muted>
              You're already in a pnpm monorepo that pulls <code>@osd/ui</code> as a workspace dep.
              Subpath imports only; no barrel re-export (per root/osd#20 — barrel tree-shaking
              breaks the per-component bundle).
            </Text>
          </Card>

          <Card>
            <Heading level={3}>2. Browser-pack consumer (no build)</Heading>
            <Text muted>
              Drop the served bundle into a plain HTML page via an import map. Useful for
              prototypes, third-party demos, or anywhere a full bundler is overkill.
            </Text>
          </Card>

          <Card>
            <Heading level={3}>3. Vite / esbuild consumer</Heading>
            <Text muted>
              Install the tarball directly from the served URL — npm fetches it over HTTPS and
              unpacks it into node_modules. Same component API as the workspace consumer; only the
              install step differs.
            </Text>
          </Card>
        </Stack>
      </Section>

      <Section
        title="Workspace consumer"
        hint="pnpm add workspace:* @osd/ui, then import each primitive by subpath"
      >
        <CodeBlock
          language="bash"
          title="Install"
          code={`# In your package.json (the app that consumes the design system):
{
  "dependencies": {
    "@osd/ui": "workspace:*",
    "react": "^18.3.0",
    "react-dom": "^18.3.0"
  }
}

pnpm install
`}
        />

        <CodeBlock
          language="tsx"
          title="app/entry.tsx — import CSS once, primitives by subpath"
          code={`import '@osd/ui/styles.css';

import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Stack } from '@osd/ui/stack';
import { Tooltip } from '@osd/ui/tooltip';
// …import every primitive you use by its subpath.

export function App() {
  return (
    <Stack gap={4}>
      <Card>
        <Tooltip content="Copies the URL">
          <Button variant="primary">Copy</Button>
        </Tooltip>
      </Card>
    </Stack>
  );
}
`}
        />

        <CodeBlock
          language="css"
          title="Override design tokens at any scope"
          code={`/* Re-theme without rebuilding — override the --osd-* tokens. */
:root {
  --osd-color-accent: #5b8def;
  --osd-radius-md: 8px;
  --osd-space-4: 1rem;
}
`}
        />
      </Section>

      <Section
        title="Browser-pack consumer"
        hint="Plain HTML + import map + a single <link rel='stylesheet'>"
      >
        <CodeBlock
          language="html"
          title="index.html — no build, no bundler"
          code={`<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>My page</title>
    <!-- 1. The compiled CSS. -->
    <link rel="stylesheet" href="https://bugfix.pro/_ui/ui.css" />
  </head>
  <body>
    <div id="root"></div>

    <!-- 2. React + ReactDOM from esm.sh. -->
    <script type="importmap">
      {
        "imports": {
          "react": "https://esm.sh/react@18.3.1",
          "react-dom": "https://esm.sh/react-dom@18.3.1",
          "@osd/ui": "https://bugfix.pro/_ui/index.js"
        }
      }
    </script>

    <!-- 3. Use any primitive by its served subpath. -->
    <script type="module">
      import { Button } from '@osd/ui';
      // …or import the per-component subpath:
      // import { Button } from 'https://bugfix.pro/_ui/ui/Button.js';

      const root = document.getElementById('root');
      root.appendChild(
        Object.assign(document.createElement('button'), {
          className: 'osd-button osd-button--primary',
          textContent: 'Hello from the served bundle',
        })
      );
    </script>
  </body>
</html>
`}
        />

        <Text muted>
          See <a href="/_ui/test.html">/_ui/test.html</a> for a live demo of the same bundle
          rendered without a framework.
        </Text>
      </Section>

      <Section
        title="Vite / esbuild consumer"
        hint="npm install a tarball straight from the served URL"
      >
        <CodeBlock
          language="bash"
          title="Install the tarball straight from the URL"
          code={`# npm fetches and unpacks the tarball into node_modules.
npm install https://bugfix.pro/_ui/_tgz.tgz

# Or download first, then install from disk (offline-friendly).
curl -O https://bugfix.pro/_ui/_tgz.tgz
npm install ./_tgz.tgz

# (Future) Once @osd/ui is published to npmjs.com, this becomes:
#   npm install @osd/ui
`}
        />

        <CodeBlock
          language="tsx"
          title="app/entry.tsx — same API as the workspace consumer"
          code={`import '@osd/ui/styles.css';
import { Button } from '@osd/ui/button';
import { Stack } from '@osd/ui/stack';

export function App() {
  return (
    <Stack gap={4}>
      <Button variant="primary">Get started</Button>
    </Stack>
  );
}
`}
        />
      </Section>

      <Section title="Conventions" hint="Memorise these once; they apply across the design system">
        <Stack gap={3}>
          <Card>
            <Heading level={4}>Subpath imports only</Heading>
            <Text muted>
              Always{' '}
              <code>
                import {'{'} Button {'}'} from '@osd/ui/button'
              </code>
              , never the barrel <code>@osd/ui</code>. The barrel re-export is deprecated; it'll be
              removed in the next minor release. Subpath imports let the bundler drop unused
              components.
            </Text>
          </Card>

          <Card>
            <Heading level={4}>Import the CSS once</Heading>
            <Text muted>
              <code>import '@osd/ui/styles.css'</code> in your app's entry point. This compiles
              every primitive's SCSS into a single stylesheet that ships with the CSS variables.
            </Text>
          </Card>

          <Card>
            <Heading level={4}>Override tokens, not styles</Heading>
            <Text muted>
              Every primitive reads from <code>--osd-*</code> custom properties (colors, radii,
              spacing, typography). Override at <code>:root</code> for a global re-theme, or any
              container selector for a scoped one. Don't fight the SCSS — change the tokens.
            </Text>
          </Card>

          <Card>
            <Heading level={4}>Strict React 18</Heading>
            <Text muted>
              React 18 (no concurrent-mode opt-in). Every component is a thin{' '}
              <code>forwardRef</code> wrapper around native HTML — the rendered DOM is yours to
              inspect, style, and integrate.
            </Text>
          </Card>
        </Stack>
      </Section>

      <Section
        title="Self-hosted browser pack"
        hint="The same components ship as plain ES modules at the URL below — useful for prototypes, demos, or any site without a build step."
      >
        <Stack gap={3}>
          <Card>
            <Stack gap={2}>
              <Heading level={4}>Quick links</Heading>
              <ul>
                <li>
                  <a href={`${SELF_HOSTED_URL}/package.json`} target="_blank" rel="noreferrer">
                    {SELF_HOSTED_URL}/package.json
                  </a>{' '}
                  — the browser-resolvable package manifest (see root/osd#36 for how it's emitted).
                </li>
                <li>
                  <a href={`${SELF_HOSTED_URL}/ui.css`} target="_blank" rel="noreferrer">
                    {SELF_HOSTED_URL}/ui.css
                  </a>{' '}
                  — the compiled stylesheet (tokens + every component).
                </li>
                <li>
                  <a href={`${SELF_HOSTED_URL}/test.html`} target="_blank" rel="noreferrer">
                    {SELF_HOSTED_URL}/test.html
                  </a>{' '}
                  — live demo (plain DOM + browser ESM, no React app).
                </li>
              </ul>
            </Stack>
          </Card>

          <Text muted>
            Cache strategy: <code>{SELF_HOSTED_URL}/package.json</code> is <code>no-cache</code> so
            consumers always re-fetch and learn the current version; everything else is{' '}
            <code>max-age=31536000</code> with versioning baked into the path (<code>/v0/</code>,{' '}
            <code>/v1/</code>, …).
          </Text>
        </Stack>
      </Section>
    </Stack>
  );
}

/**
 * Absolute URL of the self-hosted pack. Pin this here so a future
 * hostname change is one place to update. The trailing slash matters
 * — every code snippet uses it.
 */
const SELF_HOSTED_URL = 'https://bugfix.pro/_ui';
